/**
 * useCloudSignatures - fetch stored (cloud) signatures for a set of tickets.
 *
 * (2026-08-20) The signature blob lives in a PRIVATE cloud container, so a
 * history row's `signatureUrl` cannot be loaded directly in an <img>. The
 * backend serves the bytes through an authenticated endpoint instead
 * (getManualCheckoutSignature -> base64 data URL). This hook fetches that data
 * URL for each visible ticket, so History can show the authoritative cloud copy
 * in preference to the on-device one.
 *
 * Design notes:
 *   - Cached across renders by docNum, so paging back and forth doesn't refetch.
 *   - A docNum with no stored signature resolves to null and is remembered, so a
 *     missing image is not retried on every render (the local copy takes over).
 *   - In-flight docNums are de-duplicated.
 *   - Best-effort: any error resolves to null; the caller falls back to local.
 */
import { useEffect, useRef, useState } from 'react';
import { useUnifiedApi } from './useUnifiedApi';

/** @returns Map docNum -> data URL for signatures found in the cloud. */
export function useCloudSignatures(docNums: string[]): Map<string, string> {
  const api = useUnifiedApi();
  const [resolved, setResolved] = useState<Map<string, string>>(() => new Map());
  // Remembers every docNum we've already fetched (hit OR miss), so we don't
  // refetch. Kept in a ref because it must not itself trigger re-renders.
  const attempted = useRef<Set<string>>(new Set());

  // Join to a stable string so the effect re-runs when the SET of docNums
  // changes, not merely when the array identity does (History re-renders on
  // every keystroke in the search box; refetching there would be wasteful).
  const docKey = docNums.join(',');

  useEffect(() => {
    if (!api || !api.getManualCheckoutSignature) return;
    const pending = docKey
      .split(',')
      .filter((d) => d && !attempted.current.has(d));
    if (pending.length === 0) return;

    let cancelled = false;
    pending.forEach((d) => attempted.current.add(d));

    (async () => {
      for (const docNum of pending) {
        let dataUrl: string | null = null;
        try {
          dataUrl = await api.getManualCheckoutSignature!(docNum);
        } catch {
          dataUrl = null;
        }
        if (cancelled) return;
        if (dataUrl) {
          setResolved((prev) => {
            const next = new Map(prev);
            next.set(docNum, dataUrl as string);
            return next;
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, docKey]);

  return resolved;
}

export default useCloudSignatures;
